import { Component, Element, Event, EventEmitter, h, Host, Listen, State, Prop } from '@stencil/core';
import { TiMetricsActionEventDetail, TiResponsive, TiResponsiveHandler, TiResponsiveViewport } from '@ticom/web-component-utils';
import { StickyHeaderChangeEventDetail } from '../sticky-header/sticky-header.interface';

@Component({
    tag: 'ti-navbar',
    styleUrl: 'navbar.scss',
    shadow: true
})
export class Navbar implements TiResponsive {
    /**
     * The navbar item that is active.
     */
    private _activeNavbarItem: HTMLLIElement;

    /**
     * Element that is represented by the active navbar item.
     */
    private _activeNavElement: HTMLElement;

    /**
     * This flag is to differentiate manual scroll and window.scrollTo initiated by click on navbar item
     */
    private _isAutoScrolling: boolean = false;

    /**
     * Kepp track of last scroll position to determine scroll direction
     */
    private _lastScrollTop: number = 0;

    /**
     * Internal list element.
     */
    private _listElement: HTMLUListElement;

    /**
     * The array of items shown on the navbar.
     */
    private _navbarItems: HTMLLIElement[] = [];

    /**
     * Value passed to window.scrollTo
     */
    private _scrollToPosition: number = 0;

    /**
     * Local setting for height of navbar when sticky.
     */
    private _stickyHeight = 42;

    @Element() hostElement!: HTMLTiNavbarElement;

    /**
     * State so when nav elements are initiated or updated component will render.
     * !! Leave uninitialized so that definition can be used as a flag.
     */
    @State() navElements: HTMLElement[];

    /**
     * State of whether the navbar is stuck to the top or not.
     */
    @State() sticky: boolean = false;

    @TiResponsiveHandler()
    @State() viewportSize: TiResponsiveViewport;

    /**
     * Whether or not the navbar is horizontally center-aligned on the page.
     *
     * @type {boolean}
     * @default false
     */
    @Prop() centered: boolean = false;

    /**
     * Whether or not the navbar is indended for top site header usage (eg. DLP template).
     *
     * @type {boolean}
     * @default false
     */
    @Prop() header: boolean = false;

    /**
     * Set the maximum width for the navbar's inner list element as a CSS size.
     * This allows the navbar to be properly centered on the page when `centered`
     * is used and the window is wider than the page width.
     * Defaults to `'1240px'` used on new TI.com folders.
     *
     * @type {string}
     * @memberof Navbar
     */
    @Prop() pageWidth: string = '1240px';

    /**
     * The amount of pixels to offset from the top of the window when scrolling to elements.
     * Set this to the pixel height of an element that floats at the top of the window,
     * such as the element in the top section of the sticky header.
     *
     * @type {number}
     * @memberof Navbar
     */
    @Prop() scrollOffset: number = 0;


    /**
     * The amount of pixels between the bottom of the the sticky navbar and the top of the
     * anchor element.
     * @type {number}
     * @memberof Navbar
     */
    @Prop() stickyMarginBottom: number = 24;

    /**
     * Property for nav element aria-label
     *
     * @type {string}
     * @default false
     */
    @Prop() tiAriaLabel: string;

    /**
     * Event for when a metrics actions occurs.
     */
    @Event() tiMetricsAction: EventEmitter<TiMetricsActionEventDetail>;

    componentWillLoad() {
        // required by TiResponsive
    }

    componentDidLoad() {
        window.setTimeout(() => {
            // set the anchor element positions - same code in window onLoad method as well, for IE bug fix
            // code in both places will make sure to scroll to correct position if there is a hash
            this._getNavElements();
        });
    }

    disconnectedCallback() {
        // required by TiResponsive
    }

    /**
     * Once the DOM has finished loading, we can find the elements that will
     * be linked in the nav.
     * When the document is complete, scroll to the hash.
     */
    @Listen('readystatechange', { target: 'document' })
    async onReadyStateChange() {
        if (document.readyState === 'interactive') {
            // find all elements using navbar ids
            this._getNavElements();
        }
    }

    /**
     * On scroll, update the active item and emit a metrics event.
     */
    @Listen('scroll', { target: 'window' })
    onScroll() {
        const top = document.documentElement.scrollTop;
        const scrollingUp = top < this._lastScrollTop;
        this._lastScrollTop = top;
        // This is a required check as the listener attaches before the componentWillLoad fires
        // We don't need to worry about the initialization as that is taken care of in componentDidLoad
        if (this.hostElement) {
            const prevActiveNavbarItem = this.sticky ? this._activeNavbarItem : undefined;
            this._updateNavbarItemStatus(scrollingUp);
            const offsetDifference = window.scrollY - this._scrollToPosition;
            if (this._isAutoScrolling && (offsetDifference <= 1 && offsetDifference >= -1)) {
                this._isAutoScrolling = false; //sets autoscrolling flag to false once it arrives at target
            } else if (!this._isAutoScrolling && this.sticky && this._activeNavbarItem !== undefined && this._activeNavbarItem !== prevActiveNavbarItem) {
                // check if 'data-navtitle' attribute is available,
                // else get 'navbar-name' attribute value
                const activeNavElementName = this._activeNavElement.getAttribute('data-navtitle') || this._activeNavElement.getAttribute('navbar-name');
                this.tiMetricsAction.emit({
                    elementName: this.hostElement.tagName,
                    eventName: 'scroll',
                    data: {
                        context: (this.sticky ? 'sticky_nav' : 'fixed_nav') + ' ' + activeNavElementName
                    }
                });
            }
        }
    }

    /**
     * Listen for sticky header state changes.
     *
     * @param event StickyHeaderChangeEventDetail
     */
    @Listen('tiStickyHeaderChange', { target: 'document' })
    onTiStickyHeaderChange(event: CustomEvent<StickyHeaderChangeEventDetail>) {
        const sticky = event.detail.sticky;
        window.setTimeout(() => {
            // update the ids on the navbar item anchors
            if (this._navbarItems) {
                this._navbarItems.forEach(element => {
                    const anchor = element.children.item(0) as HTMLAnchorElement;
                    if (sticky) {
                        anchor.id = anchor.id.replace('fixed-nav-', 'sticky-nav-');
                    } else {
                        anchor.id = anchor.id.replace('sticky-nav-', 'fixed-nav-');
                    }
                });
            }
            this.sticky = sticky;

            if (!sticky) {
                // when the nav goes un-sticky, we are at the top of the page so make the first item visible
                window.setTimeout(() => this._makeNavbarItemVisible(this._navbarItems[0]), 25); // IE needs a short delay
            }
        });
    }

    /**
     * Find all elements on the page that have the navbar-id attribute.
     */
    private _getNavElements() {
        // we only do this once after the page has loaded
        if (!this.navElements && document.readyState !== 'loading') {
            this.navElements = Array.from(document.querySelectorAll('[navbar-id]'));

            //Add id to the elements which have navbar-id with the same value
            this.navElements.forEach((item) => {
                const itemID = item.getAttribute('id');
                const navbarElemID = item.getAttribute('navbar-id');

                // If the element doesn't have an existing id the set a new id with navbarElemID
                if (itemID == null) {
                    item.setAttribute('id', navbarElemID);
                }

                // The scroll margin top for nav element
                const scrollMarginTop = this.scrollOffset + this._stickyHeight + this.stickyMarginBottom + 'px';
                item.style['scroll-margin-top'] = scrollMarginTop;
            });
        }
    }

    /**
     * Position a navbar item element within the visible width of the navbar.
     * The element position will snap to the side of the window from which
     * the element is being moved into view.
     *
     * @param navbarItem The navbar item element to be made visible
     */
    private async _makeNavbarItemVisible(navbarItem: HTMLElement): Promise<void> {
        if (navbarItem) {
            // note that scrollIntoView does not work very well here because it tries to
            // adjust vertically as well as horizontally which makes the sticky header bounce
            // and sometimes prevents the user from scrolling to the top of the page
            const elementLeft = navbarItem.getBoundingClientRect().left;
            const elementRight = elementLeft + navbarItem.offsetWidth;
            const viewportWidth = this._listElement.clientWidth;
            let positionAdjustment = 0;
            let scrollSnapAlign = 'none start';
            if (elementLeft < 0) {
                positionAdjustment = elementLeft;
            } else if (elementRight > viewportWidth) {
                positionAdjustment = elementRight - viewportWidth;
                scrollSnapAlign = 'none end';
            }
            if (positionAdjustment != 0) {
                // the element's snap alignment needs to correspond to the direction of the scroll
                // adjustment so that scroll snapping doesn't partially cut off the navbar item
                navbarItem.style['scrollSnapAlign'] = scrollSnapAlign;
                this._listElement.scrollLeft += positionAdjustment;
            }
        }

        return Promise.resolve();
    }

    /**
     * Render navbar item links based on elements with navbar-id and navbar-name attributes.
     * Applies navbar-item id to lis sticky or fixed id to anchors for metrics
     */
    private _renderNavbarItems() {
        if (this.navElements) {
            this._navbarItems = []; // reset
            return this.navElements.map((element) => {
                const name = element.getAttribute('navbar-name');
                let id = element.getAttribute('id');

                // If the element has an existing id it'll keep it, otherwise the href points to the generated id
                if (id == null) {
                    id = element.getAttribute('navbar-id');
                }

                // get the data-navtitle attribute if available, else set navbar name as data-navtitle
                const navtitle = element.getAttribute('data-navtitle') || name;
                return (!!name &&
                    <li id={`${id}-nav-item`} ref={el => el && this._navbarItems.push(el)} class="ti-navbar-item">
                        <a class="ti-navbar-item-label"
                            id={ (this.sticky ? 'sticky-nav-' : 'fixed-nav-') + id}
                            href={`#${id}`}
                            data-navtitle={navtitle}
                        >
                            {name}
                        </a>
                    </li>
                );
            }, this);
        }
    }

    /**
     * Updates the navbar items' class that indicates active status.
     */
    private _updateNavbarItemActive(element: HTMLLIElement) {
        this._activeNavbarItem = element;
        this._navbarItems.forEach(navbarItem => {
            if (element && element.id === navbarItem.id) {
                navbarItem.classList.add('ti-navbar-item-active');
                navbarItem.setAttribute('aria-current', 'true');
                this._makeNavbarItemVisible(navbarItem);
            } else {
                navbarItem.classList.remove('ti-navbar-item-active');
                navbarItem.removeAttribute('aria-current');
            }
        });
    }

    /**
     * Update the active navbar item after scrolling.
     */
    private _updateNavbarItemStatus(scrollingUp: boolean) {

        // we need to have navbar items and nav elements to highlight anything
        if (!this._navbarItems || this._navbarItems.length == 0 || !this.navElements || this.navElements.length === 0) {
            return;
        }

        // check for bottom of the page not including horizontal scrollbar if present
        if (document.documentElement.clientHeight + window.pageYOffset >= document.body.offsetHeight - 10) {
            // zoom settings sometimes cause window.pageYOffset to be a float slightly less than the integer we expect
            // we could simply round that up to fix, but instead we add 1/2-line "slack" to also allow for imprecise user
            // scrolling so that highlighting the last nav item also happens when "very close" to the bottom of the page
            const lastNavbarItem = this._navbarItems[this._navbarItems.length - 1];
            this._activeNavElement = this.navElements.find(element => lastNavbarItem.id === `${element.getAttribute('navbar-id')}-nav-item`);
            this._updateNavbarItemActive(lastNavbarItem);
        } else { // not at bottom of the page
            // find navbar element that is "active"
            // we want either the highest one that is in the window and below the navbar
            // or the closest one above the navbar
            let activeNavElement: HTMLElement;

            // the visible height of the window excludes the sticky header
            const heightAdjust = this.scrollOffset + this._stickyHeight;
            const visibleHeight = document.documentElement.clientHeight - heightAdjust;

            // the threshold is the percentage of the visible window that the section top must be above to be highlighted
            // UX rules are 24% for tablet, 40% desktop and phone - when scrolling down
            // scrolling up is 100% - section must scroll off screen before previous one is highlighted
            const threshold = scrollingUp
                ? 1
                : this.viewportSize === TiResponsiveViewport.tablet
                    ? .24
                    : .4;

            // we adjust the scrolling up factor so that the title isn't completely off-screen before the highlight changes
            // the amount is equal to half the pixel height of an H3 element's line height
            const titleAdjustment = scrollingUp
                ? this.viewportSize === TiResponsiveViewport.phone ? 16 : 18
                : 0;

            // find visible navbar element
            this.navElements.some(element => {
                if (element.hasAttribute('navbar-name')) {
                    const rect = element.getBoundingClientRect();

                    if ((rect.top - heightAdjust + titleAdjustment) <= (visibleHeight * threshold)) {
                        // element top meets threshold
                        activeNavElement = element;

                        // if the element is also below the navbar we can stop looking
                        // otherwise we continue looking in case another element is visible below this one
                        return rect.top > heightAdjust;
                    }
                }
                return false; // continue looking
            }, this);

            // highlight the element if one was found
            if (activeNavElement) {
                const activeNavbarItemElement = this._navbarItems.find(item => item.id === `${activeNavElement.id}-nav-item`);
                if (activeNavbarItemElement && this._activeNavbarItem != activeNavbarItemElement) {
                    this._activeNavElement = activeNavElement;
                    this._updateNavbarItemActive(activeNavbarItemElement);
                }
            } else {
                // No nav elements are visible e.g. scroll position is above the navbar
                this._activeNavElement = undefined;
                this._updateNavbarItemActive(undefined);
            }
        }
    }

    render() {
        return (
            <Host data-lid={ this.sticky ? 'sticky_nav' : 'fixed_nav' } class={`ti-navbar-${this.viewportSize}`}>
                <nav class={{
                    'ti-navbar-nav': true,
                    'ti-navbar-compact': (this.sticky || this.viewportSize !== TiResponsiveViewport.desktop) && !this.header,
                    'ti-navbar-sticky': this.sticky,
                    'ti-navbar-header': this.header
                }}
                aria-label={this.tiAriaLabel}
                >
                    <ul ref={el => this._listElement = el} class={{'ti-navbar-list': true, 'ti-navbar-centered': this.centered}}
                        style={{'max-width': this.pageWidth}}
                    >
                        {this._renderNavbarItems()}
                    </ul>
                </nav>
            </Host>
        );
    }
}
